import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Check, MessageCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const METHODS = [
  { id: "card", label: "Carte bancaire" },
  { id: "cash", label: "Espèces" },
  { id: "transfer", label: "Virement" },
  { id: "paypal", label: "PayPal" },
];

const Field = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</label>
    <input
      data-testid={testid}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-black border border-white/15 px-3 py-2 text-white text-sm mt-1 focus:border-[#2563EB] outline-none"
    />
  </div>
);

export const AuctionOrderForm = ({ auction, onContact, onRefresh }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [paid, setPaid] = useState(false);
  const [sellerIban, setSellerIban] = useState("");
  const [copied, setCopied] = useState(false);
  const [method, setMethod] = useState("transfer");
  const [f, setF] = useState({
    name: "", address_line: "", postal_code: "", city: "", phone: "", building: "", building_code: "", country: "France",
  });
  const total = Number(auction.current_price) + Number(auction.shipping_fee);

  useEffect(() => {
    api.get(`/auctions/${auction.id}/order`).then((r) => {
      setSellerIban(r.data.seller_iban || "");
      setPaid(!!r.data.paid);
      if (r.data.order_confirmed) setOrder(r.data.order);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [auction.id]);

  useEffect(() => {
    if (user) setF({
      name: user.name || "", address_line: user.address_line || "", postal_code: user.postal_code || "",
      city: user.city || "", phone: user.phone || "", building: user.building || "", building_code: user.building_code || "", country: user.country || "France",
    });
  }, [user]);

  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.name || !f.address_line || !f.postal_code || !f.city || !f.phone) {
      toast.error("Merci de remplir nom, adresse, code postal, ville et téléphone.");
      return;
    }
    setSubmitting(true);
    try {
      if (method === "card") {
        const r = await api.post(`/auctions/${auction.id}/card-checkout`, { ...f, payment_method: "card" });
        window.location.href = r.data.url;
        return;
      }
      const r = await api.post(`/auctions/${auction.id}/confirm-order`, { ...f, payment_method: method });
      setSellerIban(r.data.seller_iban || "");
      setOrder({ ...f, payment_method: method, total: r.data.total, currency: r.data.currency });
      toast.success("Commande confirmée ! Le vendeur a été prévenu.");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copyIban = () => {
    navigator.clipboard?.writeText(sellerIban);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return <div className="mt-4 flex items-center gap-2 text-zinc-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>;
  }

  // Paid by card → settled
  if (paid) {
    return (
      <div className="mt-4 space-y-2" data-testid="auction-order-paid">
        <p className="text-emerald-400 text-sm">✅ Œuvre réglée par carte. Merci !</p>
        <p className="text-sm text-zinc-300">L'artiste a été notifié et préparera l'expédition.</p>
      </div>
    );
  }

  // Confirmed → show payment instructions
  if (order) {
    const cashOrPaypal = {
      paypal: "Contactez le vendeur via la messagerie : il vous communiquera son numéro/email PayPal pour le règlement.",
      cash: "Réglez en espèces directement avec le vendeur — contactez-le via la messagerie pour convenir des modalités.",
    };
    return (
      <div className="mt-4 space-y-3" data-testid="auction-order-confirmed">
        <p className="text-emerald-400 text-sm">✅ Commande confirmée. Le vendeur a reçu vos coordonnées.</p>
        <p className="text-sm text-zinc-300">Total à régler : <span className="text-white">{total.toFixed(2)} {auction.currency}</span></p>
        {order.payment_method === "transfer" ? (
          <div className="border border-white/15 bg-black p-3">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Virement — IBAN du vendeur</p>
            {sellerIban ? (
              <div className="flex items-center justify-between gap-2">
                <code className="text-white text-sm break-all">{sellerIban}</code>
                <button onClick={copyIban} className="text-zinc-400 hover:text-white shrink-0" data-testid="copy-seller-iban">
                  {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            ) : (
              <p className="text-zinc-400 text-sm">Le vendeur vous communiquera son IBAN par message.</p>
            )}
          </div>
        ) : (
          <p className="text-zinc-300 text-sm">{cashOrPaypal[order.payment_method] || "Contactez le vendeur via la messagerie pour finaliser le paiement."}</p>
        )}
        <button
          data-testid="order-contact-seller"
          onClick={onContact}
          className="w-full inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white py-2.5 text-sm transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> Contacter le vendeur
        </button>
      </div>
    );
  }

  const isCard = method === "card";
  // Order form
  return (
    <div className="mt-4 space-y-3" data-testid="auction-order-form">
      <p className="text-emerald-400 text-sm">🎉 Vous avez remporté cette enchère !</p>
      <p className="text-sm text-zinc-300">Total : <span className="text-white">{total.toFixed(2)} {auction.currency}</span> (œuvre + livraison)</p>

      <Field testid="order-name" label="Nom / Prénom" value={f.name} onChange={up("name")} />
      <Field testid="order-address" label="Adresse" value={f.address_line} onChange={up("address_line")} />
      <div className="grid grid-cols-2 gap-3">
        <Field testid="order-postal" label="Code postal" value={f.postal_code} onChange={up("postal_code")} />
        <Field testid="order-city" label="Ville" value={f.city} onChange={up("city")} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field testid="order-building" label="Bâtiment / Étage" value={f.building} onChange={up("building")} />
        <Field testid="order-building-code" label="Code d'entrée" value={f.building_code} onChange={up("building_code")} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field testid="order-phone" label="Téléphone" value={f.phone} onChange={up("phone")} />
        <Field testid="order-country" label="Pays" value={f.country} onChange={up("country")} />
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wider text-zinc-500">Mode de paiement</label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {METHODS.map((m) => (
            <button
              key={m.id}
              data-testid={`order-method-${m.id}`}
              onClick={() => setMethod(m.id)}
              className={`py-2 text-xs border transition-colors ${
                method === m.id ? "border-[#2563EB] bg-[#2563EB]/15 text-white" : "border-white/15 text-zinc-400 hover:border-white/40"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <button
        data-testid="confirm-order-button"
        onClick={submit}
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white py-3 text-sm transition-colors"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {isCard ? "Payer par carte" : "Valider ma commande"}
      </button>
      <p className="text-zinc-500 text-[11px]">
        {isCard
          ? "Paiement sécurisé par carte via Stripe."
          : "Le paiement se fait directement avec le vendeur (le site ne prend aucune commission)."}
      </p>
    </div>
  );
};
